// src/utils/roleUtils.js
// ================================================================
// COMPLETE FILE — Task 4: Role-HR synchronisation helpers added.
//
// RULES:
//   NEVER check role names (user.role === 'Admin') — roles are dynamic.
//   ALWAYS check permission flags from role_permissions table via JWT.
// ================================================================

/**
 * Core permission checker.
 * Handles both Super Admin ('ALL' wildcard) and regular permission-flag users.
 * @param {object} user  - The user object from auth context / JWT
 * @param {string} perm  - Permission key, e.g. 'workforce.edit'
 */
export const hasPermission = (user, perm) => {
  if (!user) return false;
  if (user.permissions?.includes('ALL')) return true;          // Super Admin
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
};

/**
 * True  → user can see ALL employees + perform admin actions.
 * False → user sees only their own data (employee self-service view).
 *
 * TASK 4: This function is the single source of truth for HR access.
 * When a new role has 'workforce.edit' in role_permissions, the next
 * login will include that key in the JWT permissions array, and this
 * function will return true — granting Admin HR views instantly.
 */
export const canManageWorkforce = (user) => {
  return hasPermission(user, 'workforce.edit');
};

/**
 * Convenience inverse of canManageWorkforce.
 * Use this to show employee self-service UI (Clock In button, own leaves, etc.)
 */
export const isEmployeeView = (user) => {
  return !canManageWorkforce(user);
};

/**
 * Returns the employee's workforce_id from the user object.
 * Sourced from the JWT token (set in authController.js).
 *
 * TASK 4 FIX: This will now always return a valid number because
 * createWorkforce guarantees the users.workforce_id column is
 * populated before the transaction commits (belt-and-suspenders UPDATE).
 *
 * @returns {number|null}
 */
export const getWorkforceId = (user) => {
  return user?.workforce_id ?? null;
};

/**
 * TASK 3 helper — used in the main Workforce table to show a red alert
 * icon next to any employee who has one or more certifications expiring
 * within 30 days.  The `expiring_certs_count` field is injected by the
 * getWorkforce query (added in workforceController.js).
 *
 * @param {object} workforceRecord — a row from getWorkforce()
 * @returns {boolean}
 */
export const hasExpiringCerts = (workforceRecord) => {
  return parseInt(workforceRecord?.expiring_certs_count || 0) > 0;
};

/**
 * TASK 4 — Determines whether the current user's permissions allow
 * viewing any specific HR sub-module page.
 *
 * Current HR permission map:
 *   workforce.edit   → full admin HR access (attendance mgmt, payroll, shifts)
 *   workforce.view   → read-only HR access
 *   (no permission)  → employee self-service only
 *
 * Usage:  if (!canViewHRModule(user)) navigate('/403')
 */
export const canViewHRModule = (user) => {
  return hasPermission(user, 'workforce.edit') || hasPermission(user, 'workforce.view');
};

/**
 * TASK 2 — Payroll is admin-only. Only users with workforce.edit can finalize.
 */
export const canManagePayroll = (user) => {
  return hasPermission(user, 'workforce.edit');
};